/**
 * 송금 내역 필터 섹션
 */
import React, { useMemo } from 'react';
import DateInput from '@/shared/components/forms/DateInput';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { TransferFilters, SupplierOptionDto } from '../types/transfer.types';
import '@/shared/styles/search-form.css';

interface FilterSectionProps {
    filters: TransferFilters;
    supplierOptions: SupplierOptionDto[];
    onFilterChange: (filters: TransferFilters) => void;
    onSearch: () => void;
    onTransferClick: () => void;
    onExcelDownload: () => void;
    excelDownloading?: boolean;
}

const FilterSection: React.FC<FilterSectionProps> = ({
    filters,
    supplierOptions,
    onFilterChange,
    onSearch,
    onTransferClick,
    onExcelDownload,
    excelDownloading = false
}) => {
    const handleStartDateChange = (value: string) => {
        onFilterChange({ ...filters, startDate: value });
    };

    const handleEndDateChange = (value: string) => {
        onFilterChange({ ...filters, endDate: value });
    };

    const handleSupplierChange = (value: string) => {
        onFilterChange({ ...filters, suppId: value });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onSearch();
        }
    };

    // 협력사 옵션을 SelectOption 형식으로 변환
    const supplierSelectOptions: SelectOption[] = useMemo(() => {
        return supplierOptions.map(opt => ({
            value: opt.suppId,
            label: opt.suppName
        }));
    }, [supplierOptions]);

    return (
        <div className="card search-card" onKeyDown={handleKeyDown}>
            <div className="card-body">
                <div className="search-form transfer-search-form">
                    {/* 기간 필터 */}
                    <div className="form-group date-range-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-calendar-alt" />
                                기간
                            </label>
                            <div className="date-range-inputs">
                                <DateInput
                                    id="transferStartDate"
                                    value={filters.startDate}
                                    onChange={handleStartDateChange}
                                />
                                <span className="date-separator">~</span>
                                <DateInput
                                    id="transferEndDate"
                                    value={filters.endDate}
                                    onChange={handleEndDateChange}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 협력사 필터 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-building" />
                                협력사
                            </label>
                            <SearchableSelect
                                options={supplierSelectOptions}
                                value={filters.suppId}
                                onChange={handleSupplierChange}
                                placeholder="협력사 검색..."
                                showAllOption={true}
                                allOptionLabel="전체"
                            />
                        </div>
                    </div>

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        <button className="btn btn-secondary" onClick={onTransferClick}>
                            <i className="fas fa-paper-plane" />
                            송금
                        </button>
                        <button
                            className="btn btn-success"
                            onClick={onExcelDownload}
                            disabled={excelDownloading}
                        >
                            {excelDownloading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin" />
                                    다운로드 중...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-file-excel" />
                                    엑셀
                                </>
                            )}
                        </button>
                        <button className="btn btn-primary" onClick={onSearch}>
                            <i className="fas fa-search" />
                            검색
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSection;
